// panels/GaugePanel.jsx
import React from 'react';
import { PanelWrapper } from './PanelWrapper';

function getColor(value, thresholds) {
  for (const t of thresholds) {
    if (value <= t.at) return t.color;
  }
  return thresholds[thresholds.length - 1]?.color || '#3b82f6';
}

function SvgGauge({ value, min = 0, max = 100, color, unit = '' }) {
  const pct = Math.min(1, Math.max(0, (value - min) / (max - min)));
  const sweep = pct * 180;

  const CX = 100, CY = 90, R = 68, SW = 13;

  function polarXY(angleDeg, r) {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: CX + r * Math.cos(rad), y: CY - r * Math.sin(rad) };
  }

  const bgStart  = polarXY(180, R);
  const bgEnd    = polarXY(0,   R);
  const valEnd   = polarXY(180 - sweep, R);
  const largeArc = sweep > 180 ? 1 : 0;

  const bgPath  = `M ${bgStart.x} ${bgStart.y} A ${R} ${R} 0 0 1 ${bgEnd.x} ${bgEnd.y}`;
  const valPath = sweep > 0
    ? `M ${bgStart.x} ${bgStart.y} A ${R} ${R} 0 ${largeArc} 1 ${valEnd.x} ${valEnd.y}`
    : null;

  // Tick marks
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => {
    const angle = 180 - t * 180;
    const outer = polarXY(angle, R + 6);
    const inner = polarXY(angle, R - 3);
    return { outer, inner, label: Math.round(min + t * (max - min)), angle, t };
  });

  return (
    <svg viewBox="0 0 200 110" style={{ width: '100%', maxWidth: 200, display: 'block', margin: '0 auto' }}>
      {/* Background track */}
      <path d={bgPath} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={SW} strokeLinecap="round" />

      {/* Value arc */}
      {valPath && (
        <path d={valPath} fill="none" stroke={color} strokeWidth={SW} strokeLinecap="round"
          style={{ filter: `drop-shadow(0 0 6px ${color}80)` }} />
      )}

      {/* Tick marks */}
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={t.inner.x} y1={t.inner.y} x2={t.outer.x} y2={t.outer.y}
            stroke="rgba(255,255,255,0.2)" strokeWidth={1} />
        </g>
      ))}

      {/* Value text */}
      <text x={CX} y={CY + 4} textAnchor="middle" fill={color}
        fontSize={26} fontWeight={700} fontFamily="'Space Mono', monospace">
        {typeof value === 'number' ? value.toFixed(value >= 100 ? 0 : 1) : value}
      </text>
      <text x={CX} y={CY + 20} textAnchor="middle" fill="#64748b" fontSize={10}>
        {unit}
      </text>

      {/* Min / max labels */}
      <text x={CX - R - 6} y={CY + 4} textAnchor="middle" fill="#64748b" fontSize={9}>{min}</text>
      <text x={CX + R + 6} y={CY + 4} textAnchor="middle" fill="#64748b" fontSize={9}>{max}</text>
    </svg>
  );
}

const DEFAULT_THRESHOLDS = [
  { at: 5,   color: '#22c55e' },
  { at: 15,  color: '#f59e0b' },
  { at: 100, color: '#ef4444' },
];

export function GaugePanel({ title, value, min = 0, max = 100, unit = '', thresholds = DEFAULT_THRESHOLDS, loading, datasource }) {
  const numVal = parseFloat(value) || 0;
  const color  = getColor(numVal, thresholds);

  // Threshold legend
  const ranges = thresholds.map((t, i) => ({
    label: i === 0 ? `0 – ${t.at}` : `${thresholds[i - 1].at} – ${t.at}`,
    color: t.color,
  }));

  return (
    <PanelWrapper title={title} datasource={datasource} loading={loading}>
      <div style={{ padding: '12px 8px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <SvgGauge value={numVal} min={min} max={max} color={color} unit={unit} />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          {ranges.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-3)' }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.color, display: 'inline-block' }} />
              {r.label}
            </div>
          ))}
        </div>
      </div>
    </PanelWrapper>
  );
}
